import { toast } from "react-toastify";
import {
  OBJECT_TYPES,
  CORRIDOR_WALL_THICKNESS_M,
  WALL_THICKNESS_M_RENDER,
} from "../../configuratorConstants";
import { generateId } from "../../configuratorUtils";
import { distance, normalizeVector } from "../../utils/geometry";

export const handleWallAction = (state, action, context) => {
  const {
    currentFloorObjects,
    currentFloorId,
    t,
    createFramelessWallWithSegments,
  } = context;

  switch (action.type) {
    case "TOGGLE_WALL_THICKNESS": {
      const { moduleId, segmentKeyInModule } = action.payload;
      const nextStateObjects = JSON.parse(JSON.stringify(currentFloorObjects));
      const module = nextStateObjects.find(
        (m) => m.id === moduleId && m.type === OBJECT_TYPES.MODULE,
      );
      if (
        module &&
        module.wallSegments &&
        module.wallSegments[segmentKeyInModule]
      ) {
        const segment = module.wallSegments[segmentKeyInModule];
        if (segment.thickness === CORRIDOR_WALL_THICKNESS_M) {
          segment.thickness = WALL_THICKNESS_M_RENDER;
        } else {
          segment.thickness = CORRIDOR_WALL_THICKNESS_M;
        }
        return {
          handled: true,
          nextStateObjects,
          needsValidation: true,
        };
      }
      return { handled: true, directState: state };
    }

    case "TOGGLE_INTERNAL_WALL_SEGMENT": {
      const { moduleId, cellX, cellY, orientation } = action.payload;
      const nextStateObjects = JSON.parse(JSON.stringify(currentFloorObjects));
      const parentObject = nextStateObjects.find((m) => m.id === moduleId);

      if (parentObject) {
        const segmentKey = `${cellX},${cellY}_${orientation}`;
        const wallCollection =
          parentObject.type === OBJECT_TYPES.MODULE
            ? parentObject.wallSegments
            : parentObject.internalPartitions;
        if (wallCollection[segmentKey]) {
          if (
            wallCollection[segmentKey].elements &&
            wallCollection[segmentKey].elements.length > 0
          ) {
            setTimeout(
              () => toast.warn(t("toasts.deleteWallWithElementsError")),
              0,
            );
            return { handled: true, directState: state };
          }
          delete wallCollection[segmentKey];
        } else {
          wallCollection[segmentKey] = {
            id: generateId("wall_"),
            type: OBJECT_TYPES.WALL_SEGMENT,
            elements: [],
            thickness: CORRIDOR_WALL_THICKNESS_M,
          };
        }
        return {
          handled: true,
          nextStateObjects,
          needsValidation: true,
        };
      }
      return { handled: true, directState: state };
    }

    case "DELETE_WALL_BY_ID": {
      const { wallId } = action.payload;
      if (!wallId) return { handled: true, directState: state };

      let objectsOnFloor = JSON.parse(JSON.stringify(currentFloorObjects));
      const wallIndex = objectsOnFloor.findIndex(
        (obj) =>
          obj.id === wallId && obj.type === OBJECT_TYPES.FRAMELESS_WALL,
      );

      if (wallIndex === -1) {
        return { handled: true, directState: state };
      }

      objectsOnFloor.splice(wallIndex, 1);
      setTimeout(() => toast.success("Стіну видалено."), 0);

      return {
        handled: true,
        directState: {
          ...state,
          projectObjects: {
            ...state.projectObjects,
            [currentFloorId]: objectsOnFloor,
          },
          selectedObjectId: null,
        },
      };
    }

    case "DELETE_WALL_SEGMENT": {
      const { segmentId, wallId } = action.payload;
      if (!segmentId || !wallId) return { handled: true, directState: state };

      const objectsOnFloor = JSON.parse(JSON.stringify(currentFloorObjects));
      const wallIndex = objectsOnFloor.findIndex(
        (obj) =>
          obj.id === wallId &&
          (obj.type === OBJECT_TYPES.FRAMELESS_WALL ||
            obj.type === OBJECT_TYPES.FRAMELESS_PARTITION),
      );

      if (wallIndex === -1) return { handled: true, directState: state };

      const wall = objectsOnFloor[wallIndex];
      if (!wall || !wall.segments || !Array.isArray(wall.segments)) {
        return { handled: true, directState: state };
      }

      if (
        wall.type === OBJECT_TYPES.FRAMELESS_PARTITION &&
        wall.isInternal === true
      ) {
        objectsOnFloor.splice(wallIndex, 1);
        setTimeout(() => toast.success("Внутрішня стіна видалена."), 0);
        return {
          handled: true,
          directState: {
            ...state,
            projectObjects: {
              ...state.projectObjects,
              [currentFloorId]: objectsOnFloor,
            },
            selectedObjectId: null,
          },
        };
      }

      const segmentIndex = wall.segments.findIndex(
        (seg) => seg.id === segmentId,
      );
      if (segmentIndex === -1) {
        return { handled: true, directState: state };
      }

      const segmentToDelete = wall.segments[segmentIndex];
      const wallLength = distance(wall.startPoint, wall.endPoint);
      const segmentStartRatio = segmentToDelete.start / wallLength;
      const segmentEndRatio =
        (segmentToDelete.start + segmentToDelete.length) / wallLength;

      const elementsToKeep = [];
      wall.segments.forEach((seg, idx) => {
        if (idx !== segmentIndex && seg.elements) {
          seg.elements.forEach((el) => {
            const elementPosition =
              seg.start / wallLength +
              (el.positionOnSegment || 0.5) * (seg.length / wallLength);
            elementsToKeep.push({
              ...el,
              positionOnSegment: elementPosition,
            });
          });
        }
      });

      if (segmentIndex === 0 || segmentIndex === wall.segments.length - 1) {
        let newStartPoint, newEndPoint;

        if (segmentIndex === 0) {
          const direction = {
            x: wall.endPoint.x - wall.startPoint.x,
            y: wall.endPoint.y - wall.startPoint.y,
          };
          const normalizedDir = normalizeVector(direction);
          const segmentLength = segmentToDelete.length;
          newStartPoint = {
            x: wall.startPoint.x + normalizedDir.x * segmentLength,
            y: wall.startPoint.y + normalizedDir.y * segmentLength,
          };
          newEndPoint = wall.endPoint;
        } else {
          const direction = {
            x: wall.endPoint.x - wall.startPoint.x,
            y: wall.endPoint.y - wall.startPoint.y,
          };
          const normalizedDir = normalizeVector(direction);
          const segmentLength = segmentToDelete.length;
          newStartPoint = wall.startPoint;
          newEndPoint = {
            x: wall.endPoint.x - normalizedDir.x * segmentLength,
            y: wall.endPoint.y - normalizedDir.y * segmentLength,
          };
        }

        const newWall = createFramelessWallWithSegments(
          newStartPoint,
          newEndPoint,
          elementsToKeep,
        );
        if (newWall) {
          objectsOnFloor[wallIndex] = newWall;
        } else {
          objectsOnFloor.splice(wallIndex, 1);
        }
      } else {
        const direction = {
          x: wall.endPoint.x - wall.startPoint.x,
          y: wall.endPoint.y - wall.startPoint.y,
        };
        const normalizedDir = normalizeVector(direction);

        const splitPoint1 = {
          x: wall.startPoint.x + normalizedDir.x * segmentStartRatio * wallLength,
          y: wall.startPoint.y + normalizedDir.y * segmentStartRatio * wallLength,
        };
        const splitPoint2 = {
          x: wall.startPoint.x + normalizedDir.x * segmentEndRatio * wallLength,
          y: wall.startPoint.y + normalizedDir.y * segmentEndRatio * wallLength,
        };

        const elementsForPart1 = [];
        const elementsForPart2 = [];

        elementsToKeep.forEach((el) => {
          if (el.positionOnSegment < segmentStartRatio) {
            el.positionOnSegment = el.positionOnSegment / segmentStartRatio;
            elementsForPart1.push(el);
          } else if (el.positionOnSegment > segmentEndRatio) {
            el.positionOnSegment =
              (el.positionOnSegment - segmentEndRatio) / (1 - segmentEndRatio);
            elementsForPart2.push(el);
          }
        });

        const wall1 = createFramelessWallWithSegments(
          wall.startPoint,
          splitPoint1,
          elementsForPart1,
        );
        const wall2 = createFramelessWallWithSegments(
          splitPoint2,
          wall.endPoint,
          elementsForPart2,
        );

        objectsOnFloor.splice(wallIndex, 1);
        if (wall1) objectsOnFloor.push(wall1);
        if (wall2) objectsOnFloor.push(wall2);
      }

      setTimeout(() => toast.success("Сендвіч видалено."), 0);

      return {
        handled: true,
        directState: {
          ...state,
          projectObjects: {
            ...state.projectObjects,
            [currentFloorId]: objectsOnFloor,
          },
          selectedObjectId: null,
        },
      };
    }

    default:
      return null;
  }
};
